import { FC } from 'react';

import { PunchupDropdown } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupDropdown';
import {
	PunchupAccordionGroup,
	PunchupAccordionSection,
	usePunchupAccordion,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import {
	ColumnMapping,
	EXPECTED_FIELDS,
	ExpectedField,
} from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal';

import styles from './BulkColumnMapping.module.css';

export interface BulkColumnMappingProps {
	/** Raw header names from the uploaded file — the selectable "actual" columns. */
	originalHeaders: string[];
	/** Current expected-field → column-index mapping (auto-detected, then overridable). */
	mapping: ColumnMapping;
	/** Override which uploaded column feeds an expected field (null = not in file). */
	onChange: (field: ExpectedField, index: number | null) => void;
}

/** Dropdown value used for the "not present in the file" option. */
const NOT_IN_FILE = -1;

/** Single-section accordion id — kept module-level so the hook's id array is stable. */
const SECTION_ID = 'columnMatching';
const SECTION_IDS = [SECTION_ID];

/**
 * Collapsible column-matching control for the bulk-upload panel. Each row labels
 * an EXPECTED field (Email, First Name, …) and offers a dropdown of the file's
 * ACTUAL column headers, so a manager can upload a CSV/Excel file with arbitrary
 * column names and map them explicitly. Seeded with the auto-detected mapping;
 * every change re-derives the editable table via the shared hook.
 *
 * Uses the shared `PunchupAccordion` primitive (not a native `<details>`) so the
 * trigger, fonts, chevron, and expand/collapse animation match the Show Dashboard
 * accordions. It is a plain disclosure here — no form, so no dirty tracking fires.
 */
export const BulkColumnMapping: FC<BulkColumnMappingProps> = ({ originalHeaders, mapping, onChange }) => {
	const accordion = usePunchupAccordion(SECTION_IDS);

	const headerOptions = [
		...originalHeaders.map((header, index) => ({ value: index, label: header || `Column ${index + 1}` })),
		{ value: NOT_IN_FILE, label: '— Not in file —' },
	];

	return (
		<PunchupAccordionGroup
			openSections={accordion.openSections}
			onToggle={accordion.toggle}
			setDirtySection={accordion.setDirtySection}
		>
			<PunchupAccordionSection
				id={SECTION_ID}
				title="Column matching"
				description="Match each expected field to a column from your file."
			>
				<div className={styles.rows}>
					{EXPECTED_FIELDS.map(({ field, label }) => (
						<div className={styles.row} key={field}>
							<span className={styles.label}>{label}</span>
							<div className={styles.control}>
								<PunchupDropdown
									options={headerOptions}
									value={mapping[field] ?? NOT_IN_FILE}
									onChange={(v) => onChange(field, Number(v) === NOT_IN_FILE ? null : Number(v))}
								/>
							</div>
						</div>
					))}
				</div>
			</PunchupAccordionSection>
		</PunchupAccordionGroup>
	);
};
